// src/pages/LandingPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SignInButton, SignUpButton, useUser } from '@clerk/clerk-react';

import { fetchSiteContent, fetchPrograms } from '../services/firebase';
import { fetchFreeVideos } from '../services/bunnyStream';
import AboutMeModal from '../components/AboutMeModal';
import MOCK_PROGRAMS from '../data/mockPrograms';


function LandingPage({ demoMode = false }) {
    if (demoMode) return <DemoLanding />;
    return <ClerkLanding />;
}

function ClerkLanding() {
    const navigate = useNavigate();
    const { isSignedIn } = useUser();
    return (
        <LandingShell isSignedIn={isSignedIn} onGoToApp={() => navigate('/dashboard')}
            cornerCta={<>
                <SignInButton mode="modal"><button className="mom-btn mom-btn-outline" style={{ padding: '9px 20px', fontSize: 14 }}>Sign In</button></SignInButton>
                <SignUpButton mode="modal"><button className="mom-btn mom-btn-primary" style={{ padding: '9px 20px', fontSize: 14 }}>Sign Up</button></SignUpButton>
            </>}>
            <SignUpButton mode="modal"><button className="mom-btn mom-btn-primary" style={{ padding: '16px 44px', fontSize: 16 }}>Sign Up</button></SignUpButton>
            <SignInButton mode="modal"><button className="mom-btn mom-btn-outline" style={{ padding: '14px 36px', fontSize: 15 }}>Sign In</button></SignInButton>
        </LandingShell>
    );
}

function DemoLanding() {
    const navigate = useNavigate();
    const demoAuth = () => { localStorage.setItem('mom_demo_auth', 'true'); navigate('/dashboard'); };
    return (
        <LandingShell isSignedIn={false} onGoToApp={() => navigate('/dashboard')}
            cornerCta={<>
                <button className="mom-btn mom-btn-outline" style={{ padding: '9px 20px', fontSize: 14 }} onClick={demoAuth}>Sign In</button>
                <button className="mom-btn mom-btn-primary" style={{ padding: '9px 20px', fontSize: 14 }} onClick={demoAuth}>Sign Up</button>
            </>}>
            <button className="mom-btn mom-btn-primary" style={{ padding: '16px 44px', fontSize: 16 }} onClick={demoAuth}>Sign Up</button>
            <button className="mom-btn mom-btn-outline" style={{ padding: '14px 36px', fontSize: 15 }} onClick={demoAuth}>Sign In</button>
        </LandingShell>
    );
}

function LandingShell({ children, isSignedIn, onGoToApp, signedInHeroButton, previewContent, cornerCta }) {
    const navigate = useNavigate();
    const [c, setC] = useState(previewContent || {});
    const [programs, setPrograms] = useState([]);
    const [showAbout, setShowAbout] = useState(false);
    // "Peek Inside" preview content. null = still loading (renders a shimmer),
    // [] = loaded-but-empty (rail hides gracefully). Never gates the section
    // shell — it paints from cached siteConfig immediately (paint-first).
    const [previewShorts, setPreviewShorts] = useState(null);
    const [previewLibrary, setPreviewLibrary] = useState(null);
    const [activeShort, setActiveShort] = useState(null);

    useEffect(() => {
        // Admin live-preview path: render the real page from the DRAFT content the
        // admin is editing — skip all network fetches. For every real visitor
        // (previewContent undefined) this is byte-identical to the original.
        if (previewContent) { setC(previewContent); setPrograms(MOCK_PROGRAMS); return; }
        fetchSiteContent().then(setC);
        fetchPrograms().then((p) => setPrograms(p || MOCK_PROGRAMS));
        // Real in-app content for the "Peek Inside" section. Best-effort — any
        // failure degrades to an empty rail (which hides itself), never an error.
        fetchFreeVideos()
            .then((v) => setPreviewShorts(Array.isArray(v) ? v.slice(0, 10) : []))
            .catch(() => setPreviewShorts([]));

        // Locked class library. Retry on an EMPTY result (not just on error): a
        // serverless cold-start can return [] on the first hit, and the grid hides
        // when empty — which reads as "the library section vanished". Retrying a
        // couple of times absorbs that so it reliably appears. A genuinely-empty
        // library (no recordings) simply exhausts the retries and stays hidden.
        let cancelled = false;
        (async () => {
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const r = await fetch('/api/landing-preview-classes');
                    const d = r.ok ? await r.json() : { classes: [] };
                    const classes = Array.isArray(d.classes) ? d.classes : [];
                    if (cancelled) return;
                    if (classes.length > 0 || attempt === 2) { setPreviewLibrary(classes); return; }
                } catch (e) {
                    if (cancelled) return;
                    if (attempt === 2) { setPreviewLibrary([]); return; }
                }
                await new Promise((res) => setTimeout(res, 500 * (attempt + 1))); // 0.5s, 1s
            }
        })();
        return () => { cancelled = true; };
    }, [previewContent]);

    const tiles = [
        { title: c.tile1Title, desc: c.tile1Desc, link: c.tile1Link },
        { title: c.tile2Title, desc: c.tile2Desc, link: c.tile2Link },
        { title: c.tile3Title, desc: c.tile3Desc, link: c.tile3Link },
    ].filter((t) => (t.title && t.title.trim()) || (t.desc && t.desc.trim()));

    return (
        <div style={{ fontFamily: 'var(--mom-font-serif)', background: 'var(--mom-cream)' }}>
            {!previewContent && <Helmet><title>Moments of Movement with Danielle</title></Helmet>}

            {/* ── TOP BAR: returning-member Sign In (top-right) ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, maxWidth: 1200, margin: '0 auto', padding: '18px 24px 0', position: 'relative', zIndex: 5 }}>
                {isSignedIn
                    ? <button className="mom-btn mom-btn-outline" style={{ padding: '9px 22px', fontSize: 14 }} onClick={onGoToApp}>{c.goToDashboardLabel || 'Go to Dashboard'}</button>
                    : cornerCta}
            </div>

            {/* ── HERO (title only — CTA moved below the preview) ─ */}
            {/* Soft, self-contained warm halo behind the title — a radial wash that
                fades to nothing, so it can never hard-clip into a blob the way the
                old fixed circles did once the hero shrank. */}
            <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '32px 24px 24px', position: 'relative', background: 'radial-gradient(ellipse 68% 135% at 50% -12%, color-mix(in srgb, var(--mom-primary) 8%, transparent) 0%, transparent 60%)' }}>
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 800 }}>
                    <p className="slide-up" style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mom-primary)', marginBottom: 20, fontWeight: 600 }}>{c.heroTagline || ''}</p>
                    <h1 className="slide-up stagger-1" style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(40px, 7vw, 72px)', fontWeight: 700, fontStyle: 'italic', lineHeight: 1.1, color: 'var(--mom-charcoal)', marginBottom: 12 }}>{c.heroTitle || 'moments of movement'}</h1>
                    <p className="slide-up stagger-2" style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)' }}>{c.heroSubtitle || ''}</p>
                </div>
            </section>

            {/* ── PEEK INSIDE (real in-app content) ──────────── */}
            <AppPreviewSection
                c={c}
                isPreview={!!previewContent}
                shorts={previewShorts}
                library={previewLibrary}
                onPlayShort={setActiveShort}
            />

            {/* ── HERO CTA (description + Sign Up / Sign In) ──── */}
            <section style={{ textAlign: 'center', padding: '8px 24px 80px', background: 'var(--mom-white)' }}>
                <div style={{ maxWidth: 640, margin: '0 auto' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 'clamp(16px, 2.5vw, 20px)', color: 'var(--mom-warm-gray)', margin: '0 auto 36px', lineHeight: 1.7 }}>{isSignedIn ? (c.heroDescriptionSignedIn || c.heroDescription || '') : (c.heroDescription || '')}</p>
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {isSignedIn ? (
                            <button className="mom-btn mom-btn-primary" onClick={onGoToApp}>{c.goToDashboardLabel || 'Go to Dashboard'}</button>
                        ) : children}
                    </div>
                </div>
            </section>

            {/* ── FEATURES + TILES ─────────────────────────── */}
            {(tiles.length > 0 || c.featuresHeading) && (
                <section style={{ padding: '72px 24px 100px', background: 'var(--mom-white)' }}>
                    <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 4vw, 40px)', fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 12 }}>{c.featuresHeading || ''}</h2>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', color: 'var(--mom-warm-gray)', fontSize: 17, maxWidth: 500, margin: '0 auto 64px', lineHeight: 1.7 }}>{c.featuresSubheading || ''}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'center', alignItems: 'stretch' }}>
                            {tiles.map((t, i) => (
                                <div key={i} style={{ flex: '1 1 260px', maxWidth: tiles.length === 1 ? 400 : 340, display: 'flex' }}>
                                    <TileCard tile={t} delay={i * 0.15} onAboutClick={() => setShowAbout(true)} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── METHOD ─────────────────────────────────────── */}
            <section style={{ padding: '100px 24px', background: 'var(--mom-charcoal)', color: '#fff' }}>
                <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--mom-primary-light)', marginBottom: 20 }}>{c.methodLabel || 'The Method'}</p>
                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(32px, 5vw, 52px)', fontStyle: 'italic', fontWeight: 700, marginBottom: 32, lineHeight: 1.15 }}>{c.methodTitle || 'The 5 to 20 Method'}</h2>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 18, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, maxWidth: 600, margin: '0 auto 48px' }}>{c.methodDescription || ''}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap' }}>
                        {[
                            { num: c.methodStat1Num, label: c.methodStat1Label },
                            { num: c.methodStat2Num, label: c.methodStat2Label },
                            { num: c.methodStat3Num, label: c.methodStat3Label },
                            { num: c.methodStat4Num, label: c.methodStat4Label },
                        ].filter((s) => s.num && s.num.trim()).map((s, i) => (
                            <div key={i}><div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 56, fontWeight: 700, color: 'var(--mom-stat)' }}>{s.num}</div><div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>{s.label}</div></div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TESTIMONIALS ──────────────────────────────── */}
            {(() => {
                // Support both new array format and legacy numbered fields
                let allTestimonials = [];
                if (c.testimonials && Array.isArray(c.testimonials)) {
                    allTestimonials = c.testimonials.filter((t) => t.text && t.text.trim());
                } else {
                    for (let i = 1; i <= 5; i++) {
                        const text = c[`testimonial${i}Text`];
                        if (text && text.trim()) allTestimonials.push({ text, name: c[`testimonial${i}Name`] || '', detail: c[`testimonial${i}Detail`] || '' });
                    }
                }
                if (allTestimonials.length === 0) return null;
                const displayed = allTestimonials.slice(0, 5);
                const hasMore = allTestimonials.length > 5;
                return (
                    <TestimonialsSection heading={c.testimonialsHeading} subheading={c.testimonialsSubheading} displayed={displayed} allTestimonials={allTestimonials} hasMore={hasMore} />
                );
            })()}

            {/* ── CTA ────────────────────────────────────────── */}
            <section style={{ padding: '100px 24px', background: 'var(--mom-white)' }}>
                <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 4vw, 40px)', fontStyle: 'italic', marginBottom: 16, color: 'var(--mom-charcoal)' }}>{c.ctaHeading || 'Join the Movement'}</h2>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', color: 'var(--mom-warm-gray)', fontSize: 16, marginBottom: 40, lineHeight: 1.7 }}>{c.ctaSubheading || ''}</p>
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {isSignedIn ? (
                            <button className="mom-btn mom-btn-primary" style={{ padding: '16px 44px', fontSize: 16 }} onClick={onGoToApp}>{c.goToDashboardLabel || 'Go to Dashboard'}</button>
                        ) : children}
                    </div>
                </div>
            </section>

            {/* ── FOOTER ─────────────────────────────────────── */}
            <footer style={{ padding: '48px 24px', background: 'var(--mom-charcoal)', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', marginBottom: 8 }}>moments of movement</div>
                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 24 }}>with Danielle</div>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13 }}>© {new Date().getFullYear()} Moments of Movement. All rights reserved.</p>
            </footer>
            {showAbout && <AboutMeModal siteContent={c} onClose={() => setShowAbout(false)} onNavigate={navigate} />}
            {activeShort && <ShortPreviewModal video={activeShort} onClose={() => setActiveShort(null)} />}
        </div>
    );
}

function TileCard({ tile, delay, onAboutClick }) {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    const hasLink = tile.link && tile.link.trim();
    const linkVal = hasLink ? tile.link.trim() : '';
    const isInternal = hasLink && linkVal.startsWith('/');
    // Detect about links — both "/about" and full URLs ending in /about
    const isAboutLink = hasLink && (linkVal === '/about' || linkVal.endsWith('/about') || linkVal.endsWith('/about/'));

    // Custom link label for about tiles
    const linkLabel = isAboutLink ? 'Meet Danielle →' : 'Learn more →';

    const content = (
        <div className="slide-up" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            style={{
                background: 'linear-gradient(180deg, var(--mom-tile-bg, #ffffff) 0%, #fbfcfd 40%, #f6f8fa 100%)',
                borderRadius: 24,
                padding: '36px 32px 32px',
                textAlign: 'center',
                animationDelay: `${delay}s`,
                cursor: hasLink ? 'pointer' : 'default',
                transform: hovered ? 'translateY(-4px) scale(1.01)' : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: hovered
                    ? '0 12px 32px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04), 0 20px 40px color-mix(in srgb, var(--mom-primary) 8%, transparent)'
                    : '0 4px 16px rgba(0,0,0,0.05), 0 2px 6px rgba(0,0,0,0.03)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                position: 'relative',
                overflow: 'hidden',
            }}>
            {/* Specular highlight */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 100%)', pointerEvents: 'none', borderRadius: '24px 24px 0 0' }} />
            {/* Subtle inner border for glass feel */}
            <div style={{ position: 'absolute', inset: 0, borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', fontWeight: 700, marginBottom: 14, color: 'var(--mom-charcoal)' }}>{tile.title || ''}</h3>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7 }}>{tile.desc || ''}</p>
                {hasLink && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: isAboutLink ? 15 : 13, color: 'var(--mom-link)', fontWeight: isAboutLink ? 700 : 600, marginTop: 16 }}>{linkLabel}</p>}
            </div>
        </div>
    );

    if (hasLink && isAboutLink && onAboutClick) {
        return <div onClick={onAboutClick} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1, cursor: 'pointer' }}>{content}</div>;
    }
    if (hasLink && isInternal) {
        return <div onClick={() => navigate(tile.link.trim())} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1, cursor: 'pointer' }}>{content}</div>;
    }
    if (hasLink) {
        return <a href={tile.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1 }}>{content}</a>;
    }
    return content;
}

function TestimonialsSection({ heading, subheading, displayed, allTestimonials, hasMore }) {
    const [showAll, setShowAll] = useState(false);
    return (
        <>
            <section style={{ padding: '100px 24px', background: 'var(--mom-cream)' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 4vw, 40px)', fontStyle: 'italic', textAlign: 'center', color: 'var(--mom-charcoal)', marginBottom: 12 }}>{heading || 'Testimonials'}</h2>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', textAlign: 'center', color: 'var(--mom-warm-gray)', fontSize: 16, marginBottom: 56 }}>{subheading || ''}</p>
                    <TestimonialsGrid testimonials={displayed} />
                    {hasMore && (
                        <div style={{ textAlign: 'center', marginTop: 32 }}>
                            <button onClick={() => setShowAll(true)} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-link)', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}>
                                See all testimonials →
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* All testimonials modal */}
            {showAll && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowAll(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(62, 47, 47, 0.45)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }} />
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(251,252,253,0.95) 40%, rgba(246,248,250,0.97) 100%)', borderRadius: 28, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 32px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.3) inset, 0 1px 3px rgba(255,255,255,0.5) inset', maxWidth: 700, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40%', background: 'radial-gradient(ellipse 60% 70% at 30% 0%, rgba(255,255,255,0.35) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0, borderRadius: '28px 28px 0 0' }} />
                        <div style={{ padding: '28px 32px 0', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 28, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>{heading || 'Testimonials'}</h2>
                                <button onClick={() => setShowAll(false)} style={{ background: 'rgba(255,255,255,0.7)', border: 'none', fontSize: 18, color: 'var(--mom-warm-gray)', cursor: 'pointer', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>✕</button>
                            </div>
                        </div>
                        <div style={{ overflowY: 'auto', padding: '0 32px 32px', position: 'relative', zIndex: 1 }}>
                            {allTestimonials.map((t, i) => (
                                <TestimonialCard key={i} testimonial={t} size={i % 3 === 0 ? 'large' : 'small'} delay={0} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function TestimonialsGrid({ testimonials }) {
    if (!testimonials || testimonials.length === 0) return null;

    // Staggered layout sizes
    const sizes = ['large', 'small', 'small', 'small', 'large'];

    return (
        <div style={{ columns: testimonials.length <= 2 ? 1 : window.innerWidth > 768 ? 2 : 1, columnGap: 20 }}>
            {testimonials.map((t, i) => (
                <TestimonialCard key={i} testimonial={t} size={sizes[i % sizes.length]} delay={i * 0.1} />
            ))}
        </div>
    );
}

function TestimonialCard({ testimonial, size, delay }) {
    const [hovered, setHovered] = useState(false);
    const isLarge = size === 'large';

    return (
        <div className="slide-up" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            style={{ background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfd 40%, #f6f8fa 100%)', borderRadius: 24, padding: isLarge ? '36px 36px 32px' : '28px 28px 24px', marginBottom: 20, breakInside: 'avoid', boxShadow: hovered ? 'var(--shadow-lg)' : 'var(--shadow-sm)', transform: hovered ? 'translateY(-2px)' : 'none', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', animationDelay: `${delay}s`, position: 'relative' }}>
            <div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 48, color: 'var(--mom-primary)', opacity: 0.15, position: 'absolute', top: 16, left: 24, lineHeight: 1 }}>"</div>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: isLarge ? 17 : 15, color: 'var(--mom-charcoal)', lineHeight: 1.75, marginBottom: 20, position: 'relative', zIndex: 1, paddingTop: 8 }}>{testimonial.text}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg, var(--mom-primary), var(--mom-primary-light))`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700 }}>
                    {testimonial.name ? testimonial.name.charAt(0).toUpperCase() : '?'}
                </div>
                <div>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{testimonial.name || 'Anonymous'}</p>
                    {testimonial.detail && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>{testimonial.detail}</p>}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Peek Inside" — real in-app content on the landing page.
// A free-Shorts rail (tap to actually play a taste) + a locked On-Demand class
// library (real thumbnails, frosted with an unlock treatment). All copy is
// admin-editable via siteConfig with the fallbacks used below. Paints its shell
// immediately from cached siteConfig; the rails populate in place as content
// loads and hide gracefully when empty. Every piece is a module-scope component
// (never nested) per the project's no-inner-components rule.
// ─────────────────────────────────────────────────────────────────────────────

function PlayGlyph({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
        </svg>
    );
}

function LockGlyph({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="10.5" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
            <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

function AppPreviewSection({ c, isPreview, shorts, library, onPlayShort }) {
    if (c.landingPreviewEnabled === false) return null;

    const loadingShorts = shorts === null;
    const loadingLibrary = library === null;
    const hasShorts = isPreview || loadingShorts || (shorts && shorts.length > 0);
    const hasLibrary = isPreview || loadingLibrary || (library && library.length > 0);

    // Once everything has loaded and there's genuinely nothing to show, hide the
    // whole section (it would otherwise be a redundant second CTA). This is not a
    // paint gate — the shell paints during loading and only collapses post-fetch.
    if (!isPreview && !loadingShorts && !loadingLibrary && !(shorts && shorts.length) && !(library && library.length)) {
        return null;
    }

    return (
        <section style={{ padding: '24px 24px 72px', background: 'linear-gradient(180deg, var(--mom-cream) 0%, var(--mom-white) 100%)' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                {/* Free Shorts rail */}
                {hasShorts && (
                    <div style={{ marginBottom: 56 }}>
                        <RailHeading
                            title={c.landingPreviewShortsHeading || 'Free workouts you can start today'}
                            subtitle={c.landingPreviewShortsSubcopy || 'Short, follow-along sessions — no membership needed. Tap any to play.'}
                        />
                        <ShortsRail shorts={shorts} isPreview={isPreview} onPlayShort={onPlayShort} />
                    </div>
                )}

                {/* Locked On-Demand library */}
                {hasLibrary && (
                    <div>
                        <RailHeading
                            title={c.landingPreviewLibraryHeading || 'Plus the full class library'}
                            subtitle={c.landingPreviewLibrarySubcopy || 'Every live class becomes an on-demand replay you can return to anytime — unlocked with membership.'}
                        />
                        <LockedLibraryGrid library={library} isPreview={isPreview} lockedLabel={c.landingPreviewLockedLabel || 'Members'} />
                    </div>
                )}
            </div>
        </section>
    );
}

function RailHeading({ title, subtitle }) {
    return (
        <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(20px, 2.6vw, 26px)', fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)', marginBottom: 6 }}>{title}</h3>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.6 }}>{subtitle}</p>
        </div>
    );
}

function ShortsRail({ shorts, isPreview, onPlayShort }) {
    // Loading (null) or admin-preview → shimmer placeholders so the layout paints.
    const showPlaceholders = shorts === null || (isPreview && (!shorts || shorts.length === 0));
    const items = showPlaceholders ? [0, 1, 2, 3, 4, 5] : shorts;

    return (
        <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
            {items.map((v, i) => (
                showPlaceholders
                    ? <ShimmerTile key={i} width={168} ratio="9 / 16" />
                    : <ShortCard key={v.id} video={v} onPlayShort={onPlayShort} />
            ))}
        </div>
    );
}

function ShortCard({ video, onPlayShort }) {
    const [hovered, setHovered] = useState(false);
    const thumb = video.driveThumbnail;
    return (
        <button
            onClick={() => onPlayShort(video)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            aria-label={`Play ${video.title || 'workout'}`}
            style={{
                flex: '0 0 auto', width: 168, aspectRatio: '9 / 16', borderRadius: 18, overflow: 'hidden',
                position: 'relative', cursor: 'pointer', border: 'none', padding: 0, scrollSnapAlign: 'start',
                background: thumb ? `#e9e2db center/cover no-repeat url(${thumb})` : 'linear-gradient(160deg, var(--mom-primary-light), var(--mom-primary))',
                transform: hovered ? 'translateY(-4px)' : 'none',
                transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s',
                boxShadow: hovered ? '0 16px 36px rgba(0,0,0,0.16)' : '0 6px 18px rgba(0,0,0,0.08)',
            }}
        >
            {/* Bottom scrim for title legibility */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.55) 100%)' }} />
            {/* Duration chip */}
            {video.length > 0 && (
                <span style={{ position: 'absolute', top: 10, left: 10, fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, color: '#fff', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', padding: '3px 8px', borderRadius: 20 }}>{video.length} min</span>
            )}
            {/* Play glyph */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(-50%, -50%) scale(${hovered ? 1.08 : 1})`, transition: 'transform 0.3s', width: 46, height: 46, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mom-primary)', paddingLeft: 3, boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }}>
                <PlayGlyph size={20} />
            </div>
            {/* Title */}
            <div style={{ position: 'absolute', left: 12, right: 12, bottom: 12 }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.3, textAlign: 'left', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{video.title || 'Free workout'}</p>
            </div>
        </button>
    );
}

function LockedLibraryGrid({ library, isPreview, lockedLabel }) {
    // Show fewer tiles on phones so the locked grid stays a tidy teaser (and the
    // CTA below sits higher); desktop keeps the fuller 8-tile wall.
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(max-width: 600px)').matches : false
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 600px)');
        const onChange = () => setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange); // Safari < 14
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else if (mq.removeListener) mq.removeListener(onChange);
        };
    }, []);

    const maxTiles = isMobile ? 3 : 8;
    const showPlaceholders = library === null || (isPreview && (!library || library.length === 0));
    const items = showPlaceholders ? (isMobile ? [0, 1, 2] : [0, 1, 2, 3]) : library.slice(0, maxTiles);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
            {items.map((cls, i) => (
                showPlaceholders
                    ? <ShimmerTile key={i} ratio="16 / 9" />
                    : <LockedTile key={cls.id} cls={cls} lockedLabel={lockedLabel} />
            ))}
        </div>
    );
}

function LockedTile({ cls, lockedLabel }) {
    const [hovered, setHovered] = useState(false);
    const locked = !cls.isFree;
    const thumb = cls.thumbnailUrl;
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative', aspectRatio: '16 / 9', borderRadius: 18, overflow: 'hidden',
                boxShadow: hovered ? '0 14px 32px rgba(0,0,0,0.12)' : '0 5px 16px rgba(0,0,0,0.06)',
                transform: hovered ? 'translateY(-3px)' : 'none', transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                background: '#e9e2db',
            }}
        >
            {/* Real thumbnail — shown clearly; the lock (not a blur) signals members-only */}
            {thumb && (
                <div style={{ position: 'absolute', inset: 0, background: `center/cover no-repeat url(${thumb})` }} />
            )}
            {/* Legibility scrim for the title/lock (locked tiles get a touch more) */}
            <div style={{ position: 'absolute', inset: 0, background: locked
                ? 'linear-gradient(180deg, rgba(62,47,47,0.12) 0%, rgba(62,47,47,0.5) 100%)'
                : 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.5) 100%)' }} />

            {/* Tier chip */}
            <span style={{
                position: 'absolute', top: 10, right: 10, display: 'inline-flex', alignItems: 'center', gap: 5,
                fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
                color: '#fff', background: locked ? 'rgba(0,0,0,0.4)' : 'var(--mom-success)',
                backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', padding: '4px 9px', borderRadius: 20,
            }}>
                {locked ? <LockGlyph size={12} /> : null}{locked ? lockedLabel : 'Free'}
            </span>

            {/* Center lock for locked tiles */}
            {locked && (
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -60%)', width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mom-charcoal)' }}>
                    <LockGlyph size={17} />
                </div>
            )}

            {/* Title */}
            <div style={{ position: 'absolute', left: 12, right: 12, bottom: 11 }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                    {cls.title || 'Class'}{cls.length ? ` · ${cls.length} min` : ''}
                </p>
            </div>
        </div>
    );
}

function ShimmerTile({ width, ratio }) {
    // Inline shimmer (NOT mom-skeleton, which is invisible on cream) so the rail
    // shows structure while real content loads.
    return (
        <div style={{ flex: width ? '0 0 auto' : undefined, width: width || undefined, aspectRatio: ratio, borderRadius: 18, background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.07) 37%, rgba(0,0,0,0.04) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
    );
}

function ShortPreviewModal({ video, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(62,47,47,0.55)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: -44, right: 0, background: 'rgba(255,255,255,0.85)', border: 'none', width: 36, height: 36, borderRadius: '50%', fontSize: 18, color: 'var(--mom-charcoal)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                <div style={{ width: '100%', aspectRatio: '9 / 16', maxHeight: '82vh', borderRadius: 20, overflow: 'hidden', background: '#000', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
                    {video.streamUrl && (
                        <iframe
                            title={video.title || 'Workout preview'}
                            src={video.streamUrl}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    )}
                </div>
                {video.title && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: '#fff', marginTop: 16, textAlign: 'center' }}>{video.title}</p>}
            </div>
        </div>
    );
}

export default LandingPage;
export { LandingShell };